import {useEffect, useState} from 'react';

import type {OpStore} from '../../core/opStore/types';

type LoadWeaveObjects = () => Promise<OpStore>;
type WeaveBackendModule = Pick<
  typeof import('./weaveBackend'),
  'loadWeaveObjects'
>;
type LoadWeaveBackendModule = () => Promise<WeaveBackendModule>;

/**
 * Creates a memoized first-use loader for the remote op store and its user
 * panel registrations. The dynamic module must finish registration before its
 * promise resolves, which keeps remote-provider children gated until their
 * panel specs are available.
 */
export const createWeaveObjectsLoader = (
  loadModule: LoadWeaveBackendModule
): LoadWeaveObjects => {
  let loadPromise: Promise<OpStore> | null = null;
  return () => {
    loadPromise ??= loadModule().then(module => module.loadWeaveObjects());
    return loadPromise;
  };
};

const loadWeaveObjects = createWeaveObjectsLoader(
  () => import('./weaveBackend')
);

export const useLoadWeaveObjects = (
  skip?: boolean,
  load: LoadWeaveObjects = loadWeaveObjects
):
  | {loading: true; remoteOpStore: OpStore | null}
  | {loading: false; remoteOpStore: OpStore} => {
  const [loading, setLoading] = useState(true);
  const [remoteOpStore, setRemoteOpStore] = useState<OpStore | null>(null);

  useEffect(() => {
    if (!skip) {
      load().then(loadedRemoteOpStore => {
        setRemoteOpStore(loadedRemoteOpStore);
        setLoading(false);
      });
    }
  }, [load, skip]);

  if (loading || remoteOpStore == null) {
    return {loading: true, remoteOpStore};
  }
  return {loading, remoteOpStore};
};
